<template>
  <v-container>
    <v-row class="mt-3">
      <v-col>
        <v-card elevation="0" rounded="lg" outlined>
          <v-card-title style="font-size: 1rem; line-height: 100%">
            New task
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <div class="text-body-2 mb-1">Task name</div>
                <v-text-field
                  v-model="project_name"
                  :error="errors.name.status"
                  :error-messages="errors.name.messages"
                  @focusin="resetErrors"
                  outlined
                  dense
                  label="Task name"
                  required
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <div class="text-body-2">Domains</div>
                <div class="text-body-2 mb-1" style="opacity: 0.7">
                  delimiter - newline, comma or semicolon
                </div>
                <v-textarea
                  v-model="domains"
                  :error="errors.domains.status"
                  :error-messages="errors.domains.messages"
                  @focusin="resetErrors"
                  @change="formatDomains"
                  outlined
                  name="input-7-1"
                  label="Enter domains, delimiter - newline, comma or semicolon"
                  no-resize
                  clearable
                  hide-details="auto"
                ></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-expansion-panels flat v-model="advancedPanel">
                  <v-expansion-panel
                    class="rounded-lg dct-advanced"
                    :style="`border: 1px solid ${
                      is_dark_mode
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(0, 0, 0, 0.08)'
                    }`"
                  >
                    <v-expansion-panel-header class="px-4">
                      <div
                        class="d-flex align-center flex-wrap"
                        style="gap: 6px"
                      >
                        <span class="text-body-2 font-weight-medium">
                          Advanced settings
                        </span>
                        <!-- The panel is collapsed by default, so anything moved away from its
                             default has to stay visible from the outside — above all the option
                             that spends money. Defaults stay silent to keep the header quiet. -->
                        <v-chip v-if="advanced_country_label" x-small label outlined>
                          {{ advanced_country_label }}
                        </v-chip>
                        <v-chip v-if="advanced_niche_label" x-small label outlined>
                          {{ advanced_niche_label }}
                        </v-chip>
                        <v-chip v-if="region && region.length" x-small label outlined>
                          {{ region.length }} region(s)
                        </v-chip>
                        <v-chip v-if="onlyHistoricalData" x-small label outlined>
                          Historical only
                        </v-chip>
                        <v-chip v-if="useRedirect" x-small label outlined>
                          Redirects
                        </v-chip>
                        <v-chip v-if="ahrefsApi" x-small label color="error" dark>
                          Ahrefs API
                        </v-chip>
                      </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content>
                      <div class="pt-1">
                        <div class="dct-adv-heading">Targeting</div>
                        <v-row dense>
                          <v-col cols="12">
                            <div class="dct-field-label">Preset</div>
                            <div class="d-flex align-center" style="gap: 6px">
                              <v-select
                                v-model="selectedPresetId"
                                :items="presets"
                                item-text="name"
                                item-value="id"
                                outlined
                                dense
                                clearable
                                hide-details="auto"
                                placeholder="Load a saved preset"
                                @change="apply_preset"
                              >
                                <template v-slot:item="{ item, on, attrs }">
                                  <v-list-item v-bind="attrs" v-on="on">
                                    <v-list-item-content>
                                      <v-list-item-title>
                                        {{ item.name }}
                                      </v-list-item-title>
                                    </v-list-item-content>
                                    <v-list-item-action>
                                      <v-btn
                                        icon
                                        small
                                        :loading="deletingPresetId === item.id"
                                        @click.stop="delete_preset(item)"
                                      >
                                        <v-icon small color="warning">
                                          mdi-delete-outline
                                        </v-icon>
                                      </v-btn>
                                    </v-list-item-action>
                                  </v-list-item>
                                </template>
                              </v-select>
                              <v-btn
                                text
                                small
                                color="primary"
                                class="flex-shrink-0"
                                @click="open_save_preset_dialog"
                              >
                                <v-icon left small>mdi-content-save-outline</v-icon>
                                Save
                              </v-btn>
                            </div>
                          </v-col>
                          <v-col cols="12" sm="6">
                            <div class="dct-field-label">
                              Positions &amp; traffic by country
                            </div>
                            <v-autocomplete
                              v-model="country"
                              :items="countries"
                              item-key="id"
                              outlined
                              hide-details="auto"
                              item-text="name"
                              clearable
                              dense
                              item-value="id"
                              placeholder="Type to search"
                            ></v-autocomplete>
                          </v-col>
                          <v-col cols="12" sm="6">
                            <div class="dct-field-label">Niche</div>
                            <v-autocomplete
                              v-model="niche"
                              :items="filtersData.niches"
                              item-key="id"
                              hide-details="auto"
                              clearable
                              dense
                              outlined
                              placeholder="Type to search"
                            ></v-autocomplete>
                          </v-col>
                          <v-col cols="12">
                            <div class="dct-field-label">Region</div>
                            <v-autocomplete
                              v-model="region"
                              :items="sw_countries"
                              placeholder="Type to search"
                              outlined
                              clearable
                              dense
                              item-text="name"
                              item-value="id"
                              hide-details
                              chips
                              deletable-chips
                              small-chips
                              multiple
                            ></v-autocomplete>
                          </v-col>
                        </v-row>

                        <div class="dct-adv-heading mt-5">Options</div>
                        <div class="dct-adv-group">
                          <div class="d-flex align-start">
                            <v-switch
                              dense
                              inset
                              hide-details
                              class="ma-0 pt-0 mr-3"
                              id="historical"
                              v-model="onlyHistoricalData"
                            ></v-switch>
                            <label for="historical" style="cursor: pointer">
                              <div class="text-body-2 font-weight-medium">
                                Only historical data
                              </div>
                              <div class="text-caption text--secondary">
                                Build the task from data already in the base and
                                collect nothing new.
                              </div>
                            </label>
                          </div>

                          <v-divider class="my-3"></v-divider>

                          <div class="d-flex align-start">
                            <v-switch
                              dense
                              inset
                              hide-details
                              class="ma-0 pt-0 mr-3"
                              id="redirect"
                              v-model="useRedirect"
                            ></v-switch>
                            <label for="redirect" style="cursor: pointer">
                              <div class="text-body-2 font-weight-medium">
                                Use redirect
                              </div>
                              <div class="text-caption text--secondary">
                                Merge metrics along redirect chains, so a domain
                                is judged together with the one it redirects to.
                              </div>
                            </label>
                          </div>
                        </div>

                        <div class="dct-adv-group dct-adv-group--danger mt-3">
                          <div class="d-flex align-start">
                            <v-switch
                              dense
                              inset
                              hide-details
                              color="error"
                              class="ma-0 pt-0 mr-3"
                              id="ahrefsApi"
                              v-model="ahrefsApi"
                            ></v-switch>
                            <label for="ahrefsApi" style="cursor: pointer">
                              <div
                                class="text-body-2 error--text font-weight-medium"
                              >
                                Ahrefs API
                              </div>
                              <div class="text-caption text--secondary">
                                Collects Domain Rating, Traffic, Backlinks and
                                Referring Domains through the paid official Ahrefs
                                API instead of the parser, and unlocks the Ahrefs
                                Backlinks parameter. Spends real Ahrefs quota —
                                turn it on only when you need it.
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col>
        <v-card
          elevation="0"
          outlined
          class="overflow-auto"
          min-height="246"
          rounded="lg"
        >
          <v-card-title style="font-size: 1rem; line-height: 100%">
            Select parameters
          </v-card-title>
          <v-divider />

          <v-card-text>
            <v-treeview
              v-model="selected_params"
              selectable
              @input="change_params"
              :items="params_tree"
              item-disabled="disabled"
            >
              <template v-slot:label="{ item }">
                {{ item.name }}
                <v-badge
                  v-if="item.status && item.status !== 'work'"
                  color="green"
                  :content="item.status"
                >
                </v-badge>
              </template>

              <template v-slot:append="{ item }">
                <v-tooltip max-width="300" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-if="item.info" v-bind="attrs" v-on="on">
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <span v-html="item.info"></span>
                </v-tooltip>
              </template>
            </v-treeview>

            <v-alert
              v-if="backlinks_estimate"
              dense
              text
              class="mt-3 mb-0"
              :type="backlinks_estimate.allowed ? 'info' : 'error'"
            >
              Ahrefs Backlinks forecast:
              <b>{{ backlinks_estimate.units.toLocaleString() }}</b> API units
              for {{ backlinks_estimate.domains }} domain(s), limit
              <b>{{ backlinks_estimate.limit.toLocaleString() }}</b>.
              <template v-if="backlinks_estimate.unknown > 0">
                <br />
                {{ backlinks_estimate.unknown }} domain(s) have no known
                referring-domain count yet and are counted at the maximum
                {{ backlinks_estimate.rows_per_domain }} rows each.
              </template>
              <template v-if="!backlinks_estimate.allowed">
                <br />
                <b>Over the limit — reduce the domain list to create the task.</b>
              </template>
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <div class="text-center">
          <v-btn
            color="info"
            large
            elevation="0"
            :loading="start_btn_loading"
            @click="start_collection"
          >
            Start Collection
            <v-icon right dark> mdi-play-outline </v-icon>
          </v-btn>
          <!--          <v-btn icon>-->
          <!--            <v-icon v-html="get_battery_icon"></v-icon>-->
          <!--          </v-btn>-->
        </div>
      </v-col>
    </v-row>
    <v-row v-if="errors_messages.length > 0 || error_domains.length > 0">
      <v-col>
        <div v-if="errors_messages.length > 0">
          <v-alert
            v-for="item in errors_messages"
            :key="item"
            text
            dense
            dismissible
            border="left"
            color="error"
          >
            <span v-html="item"></span>
          </v-alert>
        </div>
        <div v-if="error_domains.length > 0">
          <v-alert text dense color="error" border="left">
            <v-row align="center">
              <v-col class="grow">
                Fix invalid domains: {{ error_domains.join(", ") }}
              </v-col>
              <v-col class="shrink">
                <v-btn outlined color="error" @click="downloadInvalidDomains"
                  >Download invalid domains
                </v-btn>
              </v-col>
            </v-row>
          </v-alert>
          <div class="text-center">
            <v-btn outlined color="warning" @click="skip_invalid_domains">
              Skip invalid domains
              <v-icon right dark> mdi-play-outline </v-icon>
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row class="mt-5">
      <v-col>
        <v-card flat rounded="lg" outlined>
          <v-card-title style="font-size: 1rem; line-height: 100%">
            Latest tasks
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-0">
            <v-data-table
              :headers="table_headers"
              :items="table_items"
              :items-per-page="10"
            >
              <template v-slot:item.name="{ item }">
                <div class="text-body-2 font-weight-bold">
                  {{ item.name }}
                  <v-chip
                    v-if="item.ahrefs_api"
                    x-small
                    color="error"
                    text-color="white"
                    class="font-weight-bold ml-1"
                  >
                    AHREFS API
                  </v-chip>
                </div>
                <div class="text-body-2">{{ formatDate(item.created_at) }}</div>
              </template>
              <template v-slot:item.ahrefs_api_units="{ item }">
                <span v-if="item.ahrefs_api && item.ahrefs_api_units > 0" class="error--text">
                  {{ item.ahrefs_api_units }} units
                  <template v-if="item.ahrefs_api_cost > 0">
                    (~{{ item.ahrefs_api_cost.toFixed(4) }})
                  </template>
                </span>
                <span v-else class="text--disabled">&mdash;</span>
              </template>
              <template v-slot:item.status="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-chip
                      v-bind="attrs"
                      v-on="on"
                      small
                      label
                      outlined
                      :color="get_status_chip(item).color"
                    >
                      {{ get_status_chip(item).label }}
                    </v-chip>
                  </template>
                  <div>
                    Collected
                    <strong>
                      {{
                        item.await_collection === null
                          ? item.number_of_domains
                          : item.number_of_domains - item.await_collection
                      }}
                      of
                      {{ item.number_of_domains }}
                    </strong>
                    domains
                  </div>
                </v-tooltip>
              </template>
              <template v-slot:item.progress="{ item }">
                <div class="d-flex flex-row align-center">
                  <v-progress-linear
                    v-if="
                      get_progress(
                        item.await_collection,
                        item.number_of_domains
                      ) < 100
                    "
                    color="info"
                    indeterminate
                    rounded
                  ></v-progress-linear>
                  <v-progress-linear
                    rounded
                    v-else
                    :value="
                      get_progress(
                        item.await_collection,
                        item.number_of_domains
                      )
                    "
                    color="success"
                  ></v-progress-linear>
                  <v-btn icon @click="open_info_dialog(item)">
                    <v-icon size="20" color="grey">
                      mdi-information-outline
                    </v-icon>
                  </v-btn>
                </div>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      v-bind="attrs"
                      v-on="on"
                      :loading="item.id === pending.exportTaskId"
                      :disabled="
                        pending.exportTaskId && item.id !== pending.exportTaskId
                      "
                      @click="export_handler(item)"
                    >
                      <v-icon
                        size="20"
                        :color="get_status_data(item.status)[1]"
                      >
                        mdi-cloud-download-outline
                      </v-icon>
                    </v-btn>
                  </template>
                  <span v-if="get_status_data(item.status)[1] === 'success'">
                    Export All data
                  </span>
                  <span v-else>Export data for NOW</span>
                </v-tooltip>
                <v-btn
                  icon
                  @click="
                    delete_id = item.id;
                    dialog_delete = true;
                  "
                >
                  <v-icon size="20" color="warning">
                    mdi-delete-outline
                  </v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
        <v-dialog
          v-model="info_dialog"
          max-width="500"
          scrollable
          content-class="elevation-0"
        >
          <v-card flat outlined class="elevation-0">
            <v-card-title style="font-size: 1rem; line-height: 100%">
              Collection progress
              <v-spacer />
              <v-btn icon @click="info_dialog = false">
                <v-icon> mdi-close </v-icon>
              </v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text class="pt-5">
              <v-alert
                v-if="
                  !loadings.info_dialog_data &&
                  ahrefs_api_usage &&
                  ahrefs_api_usage.requests > 0
                "
                dense
                text
                color="error"
                border="left"
                class="mb-4"
              >
                <div class="font-weight-bold">Ahrefs API spend</div>
                <div>
                  {{ ahrefs_api_usage.requests }} request(s),
                  {{ ahrefs_api_usage.units }} API unit(s) used
                  <template v-if="ahrefs_api_usage.cost > 0">
                    &mdash; ~{{ ahrefs_api_usage.cost.toFixed(4) }}
                  </template>
                </div>
              </v-alert>
              <v-row v-if="!loadings.info_dialog_data">
                <v-col
                  cols="12"
                  v-for="item in info_dialog_data"
                  :key="item.name"
                >
                  <div>
                    <b class="mr-2">
                      {{ item.name }}
                    </b>
                    <template v-if="item.time > 0">
                      <v-icon small left class="mr-0"
                        >mdi-clock-time-eight-outline</v-icon
                      >
                      {{ item.time }}m
                    </template>
                  </div>
                  <div>
                    <v-card class="pa-0 d-flex align-center" flat>
                      <v-row dense class="d-flex align-center">
                        <v-col cols="fill" class="pa-2">
                          <v-progress-linear
                            :color="
                              parseInt(item.percent) === 100
                                ? 'success'
                                : 'info'
                            "
                            :value="item.percent"
                            rounded
                          ></v-progress-linear>
                        </v-col>
                        <v-col cols="auto" class="pa-0">
                          <v-btn
                            icon
                            title="Delete parameter"
                            @click="handleDeleteParameter(item)"
                            :loading="
                              loadings.deleteParameter.includes(item.id)
                            "
                          >
                            <v-icon>mdi-trash-can</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-card>
                  </div>
                </v-col>
              </v-row>
              <template v-else>
                <v-skeleton-loader type="list-item" />
                <v-skeleton-loader type="list-item" />
                <v-skeleton-loader type="list-item" />
                <v-skeleton-loader type="list-item" />
                <v-skeleton-loader type="list-item" />
              </template>
            </v-card-text>
            <v-divider />
            <v-card-actions>
              <v-spacer></v-spacer>

              <v-btn color="info" @click="info_dialog = false" elevation="0">
                Done
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialog_delete" max-width="290">
          <v-card>
            <v-card-title class="headline"> Are you sure? </v-card-title>
            <v-card-actions>
              <v-spacer></v-spacer>

              <v-btn text color="grey" @click="dialog_delete = false">
                Cancel
              </v-btn>

              <v-btn color="info" text @click="delete_task"> Confirm </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="presetDialog" max-width="400">
          <v-card>
            <v-card-title class="headline">Save as preset</v-card-title>
            <v-card-text>
              <div class="text-body-2 mb-1" style="opacity: 0.7">
                Saves everything except Task name and Domains: params,
                country, niche, region and the toggles above.
              </div>
              <v-text-field
                v-model="presetName"
                outlined
                dense
                autofocus
                label="Preset name"
                hide-details="auto"
                @keyup.enter="save_preset"
              ></v-text-field>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text color="grey" @click="presetDialog = false">
                Cancel
              </v-btn>
              <v-btn
                color="info"
                text
                :loading="presetSaving"
                :disabled="!presetName"
                @click="save_preset"
              >
                Save
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialog_export" max-width="560">
          <v-card>
            <v-card-title class="d-block pb-1">
              <div style="font-size: 1rem">Export task data</div>
              <div class="text-caption" style="opacity: 0.7">
                Each option is a separate file. Pick the one you need.
              </div>
            </v-card-title>

            <v-card-text class="pt-3">
              <v-radio-group
                v-model="export_type"
                hide-details
                class="ma-0 pa-0"
              >
                <v-card
                  flat
                  outlined
                  class="mb-2 px-3 py-2"
                  :key="item.key"
                  v-for="item in available_export_types"
                  :color="
                    export_type && export_type.key === item.key
                      ? 'rgba(0, 50, 230, 0.05)'
                      : ''
                  "
                  @click="export_type = item"
                >
                  <v-radio :value="item" class="ma-0">
                    <template v-slot:label>
                      <div>
                        <div class="text-body-2">{{ item.label }}</div>
                        <div class="text-caption" style="opacity: 0.7">
                          {{ item.description }}
                        </div>
                      </div>
                    </template>
                  </v-radio>
                </v-card>
              </v-radio-group>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>

              <v-btn text @click="dialog_export = false"> Cancel </v-btn>

              <v-btn
                color="green darken-1"
                text
                :disabled="!export_type"
                @click="export_file"
              >
                Export
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
    <v-dialog
      v-model="exportQueueDialog"
      width="420"
      content-class="elevation-0"
    >
      <v-card flat outlined elevation="0">
        <v-card-title>
          <span style="font-size: 0.875rem; line-height: 100%"
            >Export successfully completed
            <v-icon small class="success--text">mdi-check</v-icon></span
          >
          <v-spacer />
          <v-btn small icon @click="exportQueueDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-5">
          <div>
            You can check the export queue on the
            <a :href="serpinsiderExportQueuePagePath" target="_blank"
              >export queue page</a
            >
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";
import filtersMixin from "@/mixins/filtersMixin";

export default {
  name: "Index",
  mixins: [filtersMixin],
  data() {
    return {
      loadings: {
        info_dialog_data: false,
        deleteParameter: [],
      },
      selectedTask: null,
      presets: [],
      selectedPresetId: null,
      deletingPresetId: null,
      presetDialog: false,
      presetName: "",
      presetSaving: false,
      onlyHistoricalData: false,
      useRedirect: false,
      ahrefsApi: false,
      // undefined = the Advanced settings panel is collapsed, 0 = open.
      advancedPanel: undefined,
      project_name: undefined,
      domains: undefined,
      table_headers: [
        {
          text: "ID",
          value: "id",
          align: "center",
          sortable: false,
          width: 50,
        },
        { text: "Name", value: "name" },
        {
          text: "Ahrefs API cost",
          value: "ahrefs_api_units",
          align: "center",
          sortable: true,
          width: 130,
        },
        {
          text: "Progress",
          value: "progress",
          align: "center",
          sortable: false,
          width: 150,
        },
        {
          text: "Status",
          value: "status",
          align: "center",
          sortable: false,
          width: 100,
        },
        {
          text: "Actions",
          value: "actions",
          align: "center",
          sortable: false,
          width: 150,
        },
      ],
      table_items: [],
      niche_items: [
        { id: 1, name: "essay" },
        { id: 2, name: "casino" },
        { id: 3, name: "porn" },
        { id: 4, name: "replica" },
        { id: 5, name: "review" },
        { id: 6, name: "loan" },
        { id: 7, name: "dating" },
        { id: 8, name: "fortuneteller" },
        { id: 9, name: "sample" },
        { id: 10, name: "automotive" },
      ],
      alert_dct: true,
      start_btn_loading: false,
      params: [],
      params_items: [],
      error_domains: [],
      validated_domains: [],
      errors: {
        name: {
          status: false,
          messages: [],
        },
        domains: {
          status: false,
          messages: [],
        },
      },
      selected_params: [],
      countries: [],
      sw_countries: [],
      country: 1,
      ignore: false,
      niche: 2,
      region: [],
      selected_export_params: [],
      errors_messages: [],
      dialog_export: false,
      dialog_delete: false,
      delete_id: undefined,
      pending: {
        exportTaskId: null,
      },
      export_id: undefined,
      limits: {},
      export_types: [
        {
          key: "main",
          label: "Main data",
          id: 0,
          exportId: 23,
          description:
            "Every parameter collected for the task, one row per domain.",
        },
        {
          key: "sw_themes",
          label: "SimilarWeb themes",
          id: 11,
          exportId: 24,
          description: "Main SimilarWeb category of each domain.",
        },
        {
          key: "sw_counties",
          label: "SimilarWeb countries",
          id: 11,
          exportId: 25,
          description: "Traffic share per country, one row per domain/country.",
        },
        {
          key: "sw_keywords_statistics",
          label: "SimilarWeb keyword statistics",
          id: 11,
          exportId: 26,
          description:
            "Organic, paid and total keyword counts per domain, split desktop/mobile.",
        },
        {
          key: "sw_keywords",
          label: "SimilarWeb keywords",
          id: 11,
          exportId: 27,
          description:
            "The keywords themselves with their traffic share, one row per keyword.",
        },
        {
          key: "textrazor_topics",
          label: "TextRazor topics",
          id: 27,
          exportId: 28,
          description: "Topics TextRazor extracted for each domain.",
        },
        {
          key: "dct_ld_sellers",
          label: "LinkDetective sellers",
          id: 51,
          exportId: 38,
          description: "Sellers and their prices for each domain.",
        },
        {
          key: "ahrefs_export",
          label: "Ahrefs metrics",
          id: null,
          exportId: 46,
          requiresAhrefsApi: true,
          description:
            "DR, traffic, keywords, backlinks and referring domains collected through the paid Ahrefs API.",
        },
        {
          key: "ahrefs_backlinks",
          label: "Ahrefs backlinks",
          id: 52,
          exportId: 48,
          description:
            "The referring domains themselves, one row each, as collected by the Backlinks parameter.",
        },
        {
          key: "drop_profile",
          label: "Drop profile report",
          id: 52,
          exportId: 49,
          description:
            "One row per domain: link-type split, dofollow share, link-farm share and DR spread, computed from the collected referring domains.",
        },
      ],
      task: {
        selected_params: [],
      },
      export_type: null,
      info_dialog: false,
      info_dialog_data: [],
      ahrefs_api_usage: null,
      backlinks_estimate: null,
      backlinks_estimate_timer: null,
      exportQueueDialog: false,
      serpinsiderExportQueuePagePath:
        "https://app.serpinsider.com/export-queue",
      filtersData: {
        niches: [],
      },
    };
  },
  async mounted() {
    this.filtersData = await this.getFilters(["niches"]);

    console.log("1;", this.filtersData);
  },
  methods: {
    async handleDeleteParameter(parameter) {
      try {
        if (!this.selectedTask) {
          throw new Error("No selected task");
        }

        const url = `${this.$store.state.server_url_clear}/api/dct/task/${this.selectedTask.id}/deleteParameter/${parameter.id}`;

        let config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: this.$store.state.auth.token,
          },
        };

        this.loadings.deleteParameter.push(parameter.id);

        const response = await axios.delete(url, config);

        if (response.data.status) {
          this.info_dialog_data = this.info_dialog_data.filter(
            (v) => v.id !== parameter.id
          );
        }
      } catch (e) {
        console.error("Error while deleting parameter.", e);
      } finally {
        this.loadings.deleteParameter = this.loadings.deleteParameter.filter(
          (v) => v !== parameter.id
        );
      }
    },
    formatDate(date) {
      return new Date(date).toDateString();
    },
    open_info_dialog(task) {
      let self = this;

      let url = `${self.$store.state.server_url_clear}/api/dct/task/${task.id}`;

      let config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: self.$store.state.auth.token,
        },
      };

      this.info_dialog = true;

      this.selectedTask = task;

      this.loadings.info_dialog_data = true;
      this.ahrefs_api_usage = null;

      axios
        .get(url, config)
        .then(function (response) {
          self.info_dialog_data = response.data.params;
          self.ahrefs_api_usage = response.data.ahrefs_api_usage || null;
        })
        .finally(() => {
          this.loadings.info_dialog_data = false;
        });
    },
    get_progress(await_collection, number_of_domains) {
      if (number_of_domains === 0) {
        return 0;
      }

      if (await_collection == null) {
        await_collection = number_of_domains;
      }

      let collected = number_of_domains - await_collection;

      let percent = Math.round((100 * collected) / number_of_domains);

      return percent === 100 && collected !== number_of_domains ? 99 : percent;
    },
    change_params() {
      if (this.selected_params.indexOf(20) !== -1) {
        if (this.selected_params.indexOf(12) === -1) {
          this.selected_params.push(12);
        }
        if (this.selected_params.indexOf(13) === -1) {
          this.selected_params.push(13);
        }
        if (this.selected_params.indexOf(15) === -1) {
          this.selected_params.push(15);
        }
      }

      if (this.selected_params.indexOf(27) !== -1) {
        if (this.selected_params.indexOf(11) === -1) {
          this.selected_params.push(11);
        }
      }
    },
    get_status_data(status) {
      let arr = {
        3: ["mdi-check-all", "success"],
        // new: ['mdi-progress-clock', 'info'],
        // error: ['mdi-progress-alert', 'warning']
      };

      return arr[status] !== undefined
        ? arr[status]
        : ["mdi-clock-outline", "grey"];
    },
    get_status_chip(item) {
      if (Number(item.status) === 3) {
        return { label: "Done", color: "success" };
      }

      let collected =
        item.await_collection == null
          ? item.number_of_domains
          : item.number_of_domains - item.await_collection;

      return collected > 0
        ? { label: "Running", color: "info" }
        : { label: "Queued", color: "grey" };
    },
    open_export_dialog() {
      let self = this;

      // Which options apply depends on the task, so the default is picked here rather than in
      // data() — and something is always picked, since an Export button with nothing selected
      // has nothing to export.
      self.export_type = self.available_export_types[0] || null;
      self.dialog_export = true;
      // self.export_id = id_task

      // let url = `${self.$store.state.server_url_clear}/api/dct/params/${self.export_id}`
      //
      // let config = {
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'Authorization': this.$store.state.auth.token
      //   }
      // }
      //
      // axios.get(url, config)
      //     .then(function (response) {
      //       if (response.data.success) {
      //         self.selected_export_params = response.data.params
      //         self.dialog_export = true
      //       }
      //     });
    },
    show_export_option(item) {
      if (item.requiresAhrefsApi) {
        return !!this.task.ahrefs_api;
      }

      return (
        item.id === 0 || this.task.selected_params.indexOf(item.id) !== -1
      );
    },
    export_handler(item) {
      const clone = JSON.parse(JSON.stringify(item));

      this.task.selected_params = clone.selected_params;

      this.task = { ...this.task, ...clone };

      const needsExportTypeDialog = this.export_types.some((t) => {
        if (t.requiresAhrefsApi) {
          return !!clone.ahrefs_api;
        }

        return t.id !== 0 && clone.selected_params.indexOf(t.id) !== -1;
      });

      if (!needsExportTypeDialog) {
        this.export_id = clone.id;
        this.export_file();
      } else {
        this.export_id = clone.id;
        this.open_export_dialog();
      }
    },
    export_file() {
      let id_task = this.export_id;

      if (!id_task) {
        alert("Export error: task ID is missing. Please reload the page and try again.");
        return;
      }

      this.pending.exportTaskId = this.export_id;

      let url = `${this.$store.state.server_url_clear}/api/s/export/base/create-task`;

      let config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: this.$store.state.auth.token,
        },
      };

      let post_data = {
        typeID: this.export_type?.exportId || 23,
        name: this.task.name,
        json: {
          id: id_task,
          params: this.task.selected_params,
        },
      };

      axios
        .post(url, post_data, config)
        .then((response) => {
          if (response.data.status) {
            this.dialog_export = false;
            this.exportQueueDialog = true;
          }
        })
        .finally(() => {
          this.pending.exportTaskId = null;
        });
    },
    delete_task() {
      let self = this;
      let id = self.delete_id;

      let url = `${self.$store.state.server_url_clear}/api/dct/task/${id}`;

      let config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: self.$store.state.auth.token,
        },
      };

      axios.delete(url, config).finally(() => {
        self.dialog_delete = false;
        self.fetch_latest_tasks();
      });
    },
    fetch_limits() {
      let self = this;

      let url = `${self.$store.state.server_url_clear}/api/dct/limits`;

      let config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: this.$store.state.auth.token,
        },
      };

      axios.get(url, config).then(function (response) {
        self.limits = response.data;
      });
    },
    fetch_countries() {
      let self = this;

      let url = `${self.$store.state.server_url_clear}/api/dct/countries`;

      let config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: this.$store.state.auth.token,
        },
      };

      axios
        .get(url, config)
        .then(function (response) {
          self.countries = response.data;
        })
        .catch(() => (self.sheet = true));
    },
    fetch_sw_countries() {
      let self = this;

      let url = `${self.$store.state.server_url_clear}/api/dct/sw-countries`;

      let config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: this.$store.state.auth.token,
        },
      };

      axios
        .get(url, config)
        .then(function (response) {
          self.sw_countries = response.data;
        })
        .catch(() => (self.sheet = true));
    },
    // Drops params the API gates behind the Ahrefs API toggle whenever that toggle is off, so a
    // selection the backend would refuse never sits invisibly in the tree. Assigns only on an
    // actual change — this runs from the selected_params watcher.
    enforce_ahrefs_api_gate() {
      if (this.ahrefsApi) {
        return;
      }

      const allowed = this.selected_params.filter(
        (id) => !this.ahrefs_api_param_ids.includes(id)
      );

      if (allowed.length !== this.selected_params.length) {
        this.selected_params = allowed;
      }
    },
    // Forecasts the Ahrefs units the backlinks parameter would spend on the current domain list.
    // Debounced because it fires on every keystroke in the domains textarea.
    refresh_backlinks_estimate() {
      let self = this;

      clearTimeout(self.backlinks_estimate_timer);

      if (!self.backlinks_selected) {
        self.backlinks_estimate = null;
        return;
      }

      self.backlinks_estimate_timer = setTimeout(() => {
        self.validateDomains();

        if (self.validated_domains.length === 0) {
          self.backlinks_estimate = null;
          return;
        }

        let url = `${self.$store.state.server_url_clear}/api/dct/task/backlinks-estimate`;

        let config = {
          headers: {
            "Content-Type": "application/json",
            Authorization: self.$store.state.auth.token,
          },
        };

        axios
          .post(url, { domains: self.validated_domains }, config)
          .then((response) => {
            self.backlinks_estimate = response.data;
          })
          .catch(() => {
            self.backlinks_estimate = null;
          });
      }, 500);
    },
    fetch_allowed_params() {
      let self = this;

      let url = `${self.$store.state.server_url_clear}/api/dct/params`;

      let config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: this.$store.state.auth.token,
        },
      };

      axios
        .get(url, config)
        .then(function (response) {
          if (response.data.success) {
            self.params_items = response.data.params;
          }
        })
        .catch(() => (self.sheet = true));
    },
    fetch_presets() {
      let self = this;

      let url = `${self.$store.state.server_url_clear}/api/dct/preset`;

      let config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: this.$store.state.auth.token,
        },
      };

      axios.get(url, config).then(function (response) {
        if (response.data.success) {
          self.presets = response.data.items;
        }
      });
    },
    open_save_preset_dialog() {
      this.presetName = "";
      this.presetDialog = true;
    },
    save_preset() {
      let self = this;

      if (!self.presetName) {
        return;
      }

      self.presetSaving = true;

      let url = `${self.$store.state.server_url_clear}/api/dct/preset`;

      let config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: self.$store.state.auth.token,
        },
      };

      let data = {
        name: self.presetName,
        params: self.selected_params,
        country: self.country,
        niche: self.niche,
        region: self.region,
        onlyHistoricalData: self.onlyHistoricalData,
        useRedirect: self.useRedirect,
        ahrefsApi: self.ahrefsApi,
      };

      axios
        .post(url, data, config)
        .then(function (response) {
          if (response.data.success) {
            self.presets.push(response.data.item);
            self.selectedPresetId = response.data.item.id;
            self.presetDialog = false;
          }
        })
        .finally(() => {
          self.presetSaving = false;
        });
    },
    apply_preset(presetId) {
      let preset = this.presets.find((item) => item.id === presetId);

      if (!preset) {
        return;
      }

      this.selected_params = preset.params;
      this.country = preset.country;
      this.niche = preset.niche;
      this.region = preset.region;
      this.onlyHistoricalData = preset.onlyHistoricalData;
      this.useRedirect = preset.useRedirect;
      this.ahrefsApi = preset.ahrefsApi;

      // Options the user did not switch on themselves — open the panel so the preset's choices
      // are not applied out of sight. The header chips cover every other case.
      if (preset.onlyHistoricalData || preset.useRedirect || preset.ahrefsApi) {
        this.advancedPanel = 0;
      }
    },
    delete_preset(preset) {
      let self = this;

      let url = `${self.$store.state.server_url_clear}/api/dct/preset/${preset.id}`;

      let config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: self.$store.state.auth.token,
        },
      };

      self.deletingPresetId = preset.id;

      axios
        .delete(url, config)
        .then(function (response) {
          if (response.data.success) {
            self.presets = self.presets.filter((item) => item.id !== preset.id);

            if (self.selectedPresetId === preset.id) {
              self.selectedPresetId = null;
            }
          }
        })
        .finally(() => {
          self.deletingPresetId = null;
        });
    },
    fetch_latest_tasks() {
      let self = this;

      let url = `${self.$store.state.server_url_clear}/api/dct/task`;

      let config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: this.$store.state.auth.token,
        },
      };

      axios
        .get(url, config)
        .then(function (response) {
          if (response.data.success) {
            self.table_items = response.data.items;
          }
        })
        .catch(() => (self.sheet = true));
    },
    skip_invalid_domains() {
      let self = this;

      self.resetErrors();

      self.ignore = true;

      self.domains = self.validated_domains.join("\n");

      self.start_collection();
    },
    downloadInvalidDomains() {
      let blob = new Blob([this.error_domains.join("\n")], {
        type: "text/plain",
      });

      let a = document.createElement("a");

      a.download = "error-domains";
      a.href = URL.createObjectURL(blob);
      a.dataset.downloadurl = ["text/plain", a.download, a.href].join(":");
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setTimeout(function () {
        URL.revokeObjectURL(a.href);
      }, 1500);
    },
    start_collection() {
      let self = this;

      if (!self.validate()) {
        return;
      }

      self.start_btn_loading = true;

      let url = `${self.$store.state.server_url_clear}/api/dct/task`;

      let config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: self.$store.state.auth.token,
        },
      };

      let data = {
        name: self.project_name,
        domains: self.validated_domains,
        params: self.selected_params,
        country: self.country,
        niche: self.niche,
        region: self.region,
        ignore: self.ignore,
        onlyHistoricalData: self.onlyHistoricalData,
        useRedirect: self.useRedirect,
        ahrefsApi: self.ahrefsApi,
      };

      axios
        .post(url, data, config)
        .then(function (response) {
          if (response.data.success) {
            self.project_name = undefined;
            self.domains = undefined;
            self.selected_params = [];

            self.fetch_latest_tasks();
            self.fetch_limits();
          } else {
            if (response.data.error.indexOf("Max count") !== -1) {
              self.errors_messages.push(
                `Max <strong>${self.limits.task_limit}</strong> domains per task, you are trying to add <strong>${self.validated_domains.length}</strong>`
              );
            } else if (
              response.data.error.indexOf("You have exceeded the") !== -1
            ) {
              self.errors_messages.push("You have exceeded the limit");
            } else {
              self.errors_messages.push(response.data.error);
            }
          }
        })
        .finally(() => {
          self.start_btn_loading = false;
        });
    },
    resetErrors() {
      let self = this;

      self.errors.name.status = false;
      self.errors.name.messages = [];
      self.errors.domains.status = false;
      self.errors.domains.messages = [];

      self.errors_messages = [];

      self.error_domains = [];
    },
    validate() {
      let self = this;

      self.resetErrors();

      let count_errors = 0;
      if (!self.domains) {
        self.errors_messages.push("Domains is required");
        count_errors++;
      } else if (!this.validateDomains()) {
        self.errors_messages.push("Invalid domains found");
        count_errors++;
      }
      if (self.selected_params.length === 0) {
        self.errors_messages.push("No params selected");
        count_errors++;
      }
      if (self.backlinks_over_limit) {
        self.errors_messages.push(
          `Ahrefs Backlinks would cost about <strong>${self.backlinks_estimate.units.toLocaleString()}</strong> API units, over the <strong>${self.backlinks_estimate.limit.toLocaleString()}</strong> limit. Reduce the domain list.`
        );
        count_errors++;
      }

      return count_errors === 0;
    },
    formatDomains() {
      let self = this;

      let domains =
        self.domains !== undefined
          ? self.domains
              .replaceAll(/\s+|;/gi, ",")
              .split(",")
              .map((v) => v.trim())
              .filter((v) => v.length > 0)
          : [];

      self.domains = domains
        .map((v) => {
          let domain;

          try {
            domain = new URL(v).hostname;
          } catch (e) {
            domain = new URL("http://" + v).hostname;
          }

          return domain.replace("www.", "");
        })
        .join("\n");
    },
    validateDomains() {
      let self = this;

      let validated_domains = [];
      let error_domains = [];

      let domains_arr =
        self.domains !== undefined
          ? self.domains
              .replaceAll(/\s+|;/gi, ",")
              .split(",")
              .map((v) => v.trim())
              .filter((v) => v.length > 0)
          : [];

      domains_arr.forEach((v) =>
        self.validateDomain(v)
          ? validated_domains.push(v)
          : error_domains.push(v)
      );

      self.validated_domains = validated_domains;
      self.error_domains = error_domains;

      return validated_domains.length > 0 && error_domains.length === 0;
    },
    validateDomain(domain) {
      let r = new RegExp("^([a-z0-9-]{1,63}\\.)+[A-Za-z]{2,25}$", "i");
      let m = domain.toLowerCase().match(r);

      return m && m.length > 0;
    },
  },
  computed: {
    is_dark_mode() {
      return this.$vuetify.theme.dark;
    },
    // Params the API flags as Ahrefs-API-only are greyed out until the toggle is on. The block
    // that matters is on the backend; this only keeps the user from picking something that would
    // be refused on submit.
    params_tree() {
      return this.params_items.map((group) => ({
        ...group,
        children: (group.children || []).map((child) => ({
          ...child,
          disabled: !!child.requires_ahrefs_api && !this.ahrefsApi,
        })),
      }));
    },
    ahrefs_api_param_ids() {
      return this.params_items
        .flatMap((group) => group.children || [])
        .filter((child) => child.requires_ahrefs_api)
        .map((child) => child.id);
    },
    backlinks_selected() {
      return this.selected_params.some((id) =>
        this.ahrefs_api_param_ids.includes(id)
      );
    },
    // Chips for the two fields that always carry a value: shown only once moved off the default,
    // so a collapsed panel never hides a non-standard choice.
    advanced_country_label() {
      if (this.country === 1 || this.country === null) {
        return "";
      }

      const country = this.countries.find((item) => item.id === this.country);

      return country ? country.name : "";
    },
    advanced_niche_label() {
      if (this.niche === 2 || this.niche === null) {
        return "";
      }

      const niche = this.filtersData.niches.find(
        (item) => item.value === this.niche || item.id === this.niche
      );

      return niche ? niche.text || niche.name : "";
    },
    available_export_types() {
      return this.export_types.filter((item) => this.show_export_option(item));
    },
    backlinks_over_limit() {
      return (
        this.backlinks_estimate !== null && !this.backlinks_estimate.allowed
      );
    },
    get_battery_icon() {
      let self = this;

      let percent = 100 - (100 * self.limits.used) / self.limits.month_limit;

      if (percent === 0) {
        return "mdi-battery-outline";
      } else if (percent < 40) {
        return "mdi-battery-low";
      } else if (percent < 80) {
        return "mdi-battery-medium";
      }

      return "mdi-battery-high";
    },
  },
  watch: {
    alert_dct() {},
    ahrefsApi() {
      this.enforce_ahrefs_api_gate();
      this.refresh_backlinks_estimate();
    },
    selected_params() {
      // Also covers applying a preset that carries a gated param while the toggle is already off,
      // where the ahrefsApi watcher never fires because the value did not change.
      this.enforce_ahrefs_api_gate();
      this.refresh_backlinks_estimate();
    },
    domains() {
      this.refresh_backlinks_estimate();
    },
  },
  created() {
    this.fetch_limits();
    this.fetch_latest_tasks();
    this.fetch_allowed_params();
    this.fetch_countries();
    this.fetch_sw_countries();
    this.fetch_presets();

    setInterval(this.fetch_latest_tasks, 10000);
  },
};
</script>

<style scoped></style>
